import { useState, useEffect, useRef } from "react";
import usePosOrders from "../../../services/Pos/usePosOrders";
import { toast } from "react-toastify";
import useDining from "../../../services/Order/useDining";

export default function UpiPaymentModal({
  isOpen,
  onClose,
  onConnect,
  cart = [],
  orderId,
  mobile,
  customerMobile,
  total = 0,
  placePosOrder,
  orderType,
  selectedTable,
  selectedRoom,
  userID,
}) {
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState([]);
  const [upiLink, setUpiLink] = useState("");

  const { getPaymentStatus, generatePaymentIntent } = usePosOrders();
const { refreshDiningCache } = useDining();

  const writerRef = useRef(null);
  const paymentProcessedRef = useRef(false);

  const [isConnected, setIsConnected] = useState(false);

  const normalizeOrderType = (value = "") => {
  const normalized = String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (["dining", "dinein", "dine_in"].includes(normalized)) {
    return "dining";
  }

  if (["takeaway", "takeway", "take_away"].includes(normalized)) {
    return "takeaway";
  }

  if (["homedelivery", "home_delivery"].includes(normalized)) {
    return "home_delivery";
  }

  if (["room_order", "roomorder"].includes(normalized)) {
    return "room_order";
  }

  return normalized;
};

const normalizedOrderType = normalizeOrderType(orderType);

useEffect(() => {
  if (!isOpen) return;

  paymentProcessedRef.current = false;
  setPaymentStatus([]);
  setUpiLink("");
}, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const generateUPI = async () => {
      try {
       const res = await generatePaymentIntent({
  custMobile: customerMobile || mobile || "",
  custName: "Customer",
  orderId,
  userid: userID,
});

        if (res?.paymentLink) {
          setUpiLink(res.paymentLink);
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to generate QR");
      }
    };

    generateUPI();
  }, [isOpen]);

  useEffect(() => {
    if (!upiLink) return;

    const interval = setInterval(async () => {
      await paymentCallback();
    }, 5000);

    return () => clearInterval(interval);
  }, [upiLink]);

  const handleConnect = async () => {
    try {
      const port = await navigator.serial.requestPort();
      await port.open({ baudRate: 9600 });

      const encoder = new TextEncoderStream();
      encoder.readable.pipeTo(port.writable);

      const writer = encoder.writable.getWriter();
      writerRef.current = writer;

      setIsConnected(true);

      toast.success("Machine Connected ");
    } catch (err) {
      console.error("Connection failed:", err);
      setIsConnected(false);
      toast.error("Connection failed ");
    }
  };

  const sendQRToMachine = async () => {
    if (!writerRef.current || !upiLink) {
      toast.error("Machine not connected or UPI missing");
      return;
    }

    try {
      const url = new URL(upiLink);
      const params = new URLSearchParams(url.search);

      const pa = params.get("pa");
      const tn = params.get("tn") || "Payment";
      const am = total;

      const data = `${pa}::${tn}::${am}`;

      await writerRef.current.write(data + "\n");

    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (upiLink && writerRef?.current) {
      sendQRToMachine();
    }
  }, [upiLink]);

  if (!isOpen) return null;

  const getTableNo = () => {
  if (normalizedOrderType === "dining") {
    return (
      selectedTable?.tableNo ||
      selectedTable?.table_no ||
      selectedTable?.table ||
      selectedTable?.name ||
      ""
    );
  }

  if (normalizedOrderType === "room_order") {
    return (
      selectedRoom?.roomNumber ||
      selectedRoom?.roomNo ||
      selectedRoom?.room_no ||
      selectedTable?.roomNumber ||
      selectedTable?.roomNo ||
      selectedTable?.room_no ||
      selectedTable?.tableNo ||
      selectedTable?.table_no ||
      ""
    );
  }

  return "";
};

const refreshTableRoomStatus = async () => {
  if (
    normalizedOrderType !== "dining" &&
    normalizedOrderType !== "room_order"
  ) {
    return;
  }

  try {
    await refreshDiningCache();

    setTimeout(() => {
      refreshDiningCache();
    }, 700);
  } catch (error) {
    console.error("UPI table/room refresh error:", error);
  }
};

const handlePaymentSuccess = async () => {
  if (loading) return;

  try {
    setLoading(true);

    const payload = {
      userid: userID,

      currency: "INR",
      custMobile: customerMobile || mobile || "",
      custName: "",

      orderId,
      orderType: normalizedOrderType,
      orderStatus: "completed",

      paymentMode: "upi",
      receivedAmount: Number(total || 0),

      deliveryAmount: "0",
      deliveryChargesDeductedFrom: "customer",
      deliveryPartner: "",

      tableNo: getTableNo(),

      items: cart.map((item) => ({
        productId: String(
          item.item_id ||
            item.productId ||
            "",
        ),

        sizeId: String(
          item.size_id ||
            item.sizeId ||
            item.selectedPrice?.size_id ||
            item.item_id ||
            "1",
        ),

        productName:
          item.item_name ||
          item.productName ||
          item.actualName ||
          "Item",

        actualName:
          item.item_name ||
          item.actualName ||
          item.productName ||
          "Item",

        itemPrice: String(
          item.price ||
            item.item_price ||
            item.selectedPrice?.price ||
            0,
        ),

        quantity: String(
          item.qty ||
            item.quantity ||
            1,
        ),

        customization:
          typeof item.customization === "string"
            ? item.customization
            : JSON.stringify(
                item.customizations ||
                  item.customization ||
                  [],
              ),

        hasVarient:
          item.selectedPrice?.size_id ||
          item.size_id
            ? "true"
            : "false",

        itemImage:
          item.image ||
          item.photo ||
          item.itemImage ||
          "",

        sizeWords:
          item.selectedPrice?.size ||
          item.size ||
          item.sizeWords ||
          "",
      })),
    };

    const res = await placePosOrder(payload);

    if (res?.responseCode !== "00") {
      toast.error(
        res?.responseMessage ||
          "Order placement failed",
      );
      return;
    }

    await refreshTableRoomStatus();

    toast.success("UPI Payment Successful");

    if (writerRef.current) {
      await writerRef.current.write("success\n");
    }

    onClose();
  } catch (error) {
    console.error(
      "UPI order placement failed:",
      error,
    );

    toast.error("Order placement failed");
  } finally {
    setLoading(false);
  }
};

  const handleCancel = async () => {
    try {
      if (writerRef.current) {
        await writerRef.current.write("cancel\n");
      }
    } catch (err) {
      console.error(err);
    }

    onClose();
  };

  const paymentCallback = async () => {
  if (paymentProcessedRef.current) return;

  try {
    const res = await getPaymentStatus(orderId);

    setPaymentStatus(res);

    const payment = Array.isArray(res)
      ? res[0]
      : Array.isArray(res?.response)
        ? res.response[0]
        : res?.response || res;

    const status = String(
      payment?.payment_status ||
        payment?.paymentStatus ||
        payment?.status ||
        "",
    )
      .trim()
      .toUpperCase();

    if (["SUCCESS", "PAID"].includes(status)) {
      paymentProcessedRef.current = true;

      await handlePaymentSuccess();
    }
  } catch (error) {
    console.error(
      "Payment status check failed:",
      error,
    );
  }
};

  const handleCheckPaymentClick = async () => {
    setLoading(true);
    await paymentCallback();
    setLoading(false);
  };

  return (
    <>

    <div className="fixed inset-0 z-50 flex items-center montserrat-font-1 justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl border montserrat-font-1 border-gray-100 shadow-xl p-10 w-full max-w-md flex flex-col items-center">
        {/* Icon */}
        <div className="w-14 h-14 rounded-xl border-2 montserrat-font-1 border-gray-800 flex items-center justify-center mb-5">
          <svg
            className="w-7 h-7 text-gray-800"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
            viewBox="0 0 24 24"
          >
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
            <line x1="1" y1="10" x2="23" y2="10" />
          </svg>
        </div>

        {/* Title */}
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Bumppy QR Billing
        </h2>

        {/* Message */}
        <p className="text-sm text-gray-500 text-center mb-2">
          Scan QR and complete payment
        </p>

        {upiLink && (
          <p className="text-xs text-blue-600 break-all mb-5">{upiLink}</p>
        )}

        {/* Buttons */}
        <div className="flex flex-wrap gap-2.5 justify-center">
          <button
            onClick={handleConnect}
            className="bg-teal-500 text-white px-5 py-2.5 rounded-lg"
          >
            Connect Machine
          </button>

          <button
            onClick={handleCancel}
            className="bg-gray-500 text-white px-5 py-2.5 rounded-lg"
          >
            Reset / Close
          </button>

          <button
            onClick={handleCheckPaymentClick}
            disabled={loading}
            className="bg-green-600 text-white px-5 py-2.5 rounded-lg"
          >
            {loading ? "Checking..." : "Check Payment"}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
